import { useState, useEffect } from "react";
import { DydAvatar, DydChatPanel, MASCOT_IMG } from "@/components/Dyd";
import { Button } from "@/components/ui/button";
import { X, ChatCircleDots } from "@phosphor-icons/react";
import useDraggableFAB from "@/hooks/useDraggableFAB";

/**
 * Floating mascot bubble (bottom-right) that opens an expandable chat panel.
 * Mounted globally in Layout so it appears on every authenticated page.
 * The launcher button is draggable — long-tap and drag to reposition; the
 * position is persisted in localStorage under `dyd:fab:dyd-bot`.
 */
export default function DydMascotBubble() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(false);
  // Mobile: lift above the bottom nav (~60px) + install banner / safe area so it stays accessible.
  const isMobile = typeof window !== "undefined" && window.innerWidth < 1024;
  const { style, bind, dragging } = useDraggableFAB("dyd-bot", { right: 16, bottom: isMobile ? 180 : 96 });

  // First-time hint: pulse for 5 seconds when component mounts
  useEffect(() => {
    setUnread(true);
    const t = setTimeout(() => setUnread(false), 5000);
    return () => clearTimeout(t);
  }, []);

  const handleClick = (e) => {
    if (dragging) { e.preventDefault(); return; }
    setOpen(true);
    setUnread(false);
  };

  return (
    <>
      {/* Floating launcher button (draggable) */}
      {!open && (
        <button
          type="button"
          onClick={handleClick}
          {...bind}
          style={{ ...style, borderRadius: "50%" }}
          className="z-[55] w-16 h-16 sm:w-20 sm:h-20 bg-[#0A0A0A] border-2 border-[#CCFF00] hover:scale-105 active:scale-95 transition-transform shadow-[0_0_24px_rgba(204,255,0,0.4)] flex items-center justify-center tap-target select-none"
          data-testid="dyd-mascot-launcher"
          aria-label="Abrir DYD Bot (arrastra para mover)"
        >
          <DydAvatar src={MASCOT_IMG} size={56} testId="dyd-mascot-img" />
          {unread && <span className="absolute top-1 right-1 w-3 h-3 bg-[#FF3B30] rounded-full animate-pulse" />}
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="dyd-panel-mobile fixed z-[55] bg-[#141414] border-2 border-[#CCFF00] shadow-2xl flex flex-col animate-slide-left"
          data-testid="dyd-mascot-panel"
        >
          {/* Header */}
          <div className="flex items-center gap-3 p-3 border-b border-white/10 bg-[#0A0A0A]">
            <div className="w-12 h-12 bg-[#0A0A0A] border border-[#CCFF00]/40 flex items-center justify-center shrink-0" style={{ borderRadius: "50%" }}>
              <DydAvatar src={MASCOT_IMG} size={42} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00]">DYD Bot</p>
              <p className="font-['Barlow_Condensed'] text-lg font-black uppercase leading-tight">Asistencia rápida</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setOpen(false)}
              className="text-gray-400 hover:text-white"
              data-testid="dyd-mascot-close"
              aria-label="Cerrar DYD"
            >
              <X size={20} />
            </Button>
          </div>

          {/* Chat */}
          <div className="flex-1 min-h-0">
            <DydChatPanel role="mascot" compact />
          </div>
        </div>
      )}
    </>
  );
}

// Re-export icon for tree-shaking (used as fallback above)
export { ChatCircleDots };
